// todo split this file for lotto and sportsbook
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';

import {stripPathLang, stripRoutingPrefix} from '#core/utils/url-utils.js';
import {formatTranslationString} from '#core/utils/utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';

import {SEO} from './index.js';

const StringUtils = MojitoCore.Base.StringUtils;
const AbstractHeadMetaFactory = MojitoPresentation.Components.AbstractHeadMetaFactory;
const NavigationPathBuilder = MojitoPresentation.Base.Navigation.NavigationPathBuilder;

export class BaseHeadMetaFactory extends AbstractHeadMetaFactory {
    constructor(l10n) {
        super();
        this.l10n = l10n;
        this.routeResolver = null; // will be overridden outside (temp workaround for MOJ-11146)
    }

    setParameters(context) {
        this.routeResolver = context.routeResolver;
    }
    resolveKey(key, data) {
        const str = StringUtils.resolveString(key, this.l10n, true);
        const phrase = data.localizedParams ? formatTranslationString(str, ...data.localizedParams.values()) : str;

        return phrase;
    }
    // ---------
    // eslint-disable-next-line no-unused-vars
    getTranslation(data, suffix) {
        return '';
    }
    // ---------
    getTitle(data) {
        return this.getTranslation(data, 'TITLE');
    }

    getKeywords(data) {
        return this.getTranslation(data, 'KEYWORDS');
    }

    getDescription(data) {
        return this.getTranslation(data, 'DESCRIPTION');
    }

    // eslint-disable-next-line no-unused-vars
    getCanonical(data) {
        // todo get pahtLang from Portal
        const sportsbookRootURL = stripPathLang(ApplicationConfig.initParams.routingPrefix);

        return new URL(
            `${sportsbookRootURL === '/' ? '' : sportsbookRootURL}${this.getRelativeCanonical()}`,
            window.location.origin
        ).toString();
    }

    // Returns the canonical URL without window.location.origin
    getRelativeCanonical() {
        return stripRoutingPrefix(ApplicationConfig.initParams.routingPrefix, window.location.pathname);
    }

    // Returns array of meta elements
    getMeta(custom = []) {
        return SEO.meta.all.concat(custom);
    }

    // Returns array of image objects for the Open Graph protocol
    getOpenGraphImages() {
        return SEO.openGraphImages;
    }
}

export class HomePageHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.HOME.' + suffix, data);
    }
    getRelativeCanonical() {
        return '';
    }
    getMeta() {
        return super.getMeta(SEO.meta.home);
    }
}

export class BadRouteHeadMetaFactory extends BaseHeadMetaFactory {
    getCanonical() {
        return null;
    }
    getRelativeCanonical() {
        return null;
    }
    getMeta() {
        return super.getMeta(SEO.meta.badRoute);
    }
    getOpenGraphImages() {
        return [];
    }
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.BAD_ROUTE.' + suffix, data);
    }
}

// sportName - localized params
export class SportHeadMetaFactory extends BaseHeadMetaFactory {
    setParameters(context) {
        super.setParameters(context);
        const {sportId, type} = context.data.params;
        this.subCategory = undefined;
        this.sportCategory = this.#getSportCategory(sportId);
        this.isLeaguePage = !!type;
    }

    #getSportCategory(sportId) {
        const competitionsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportCompetitions(sportId));
        if (location.pathname.startsWith(competitionsPath)) {
            return 'COMPETITIONS';
        }
        const outrightsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportOutright(sportId));
        if (location.pathname.startsWith(outrightsPath)) {
            return 'OUTRIGHTS';
        }
        const matchesPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportMatches(sportId));
        if (location.pathname.startsWith(matchesPath)) {
            const sportCategory = 'MATCHES';
            const lastURLSegment = location.pathname.split('/').pop().toUpperCase();

            // update subCategory with proper filter if available ex. today,inplay,streaming
            if (sportCategory !== lastURLSegment) {
                this.subCategory = lastURLSegment;
            }

            return sportCategory;
        }
        const meetingsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportMeetings(sportId));
        if (location.pathname.startsWith(meetingsPath)) {
            const sportCategory = 'MEETINGS';
            const lastURLSegment = location.pathname.split('/').pop().toUpperCase();

            // update subCategory with proper filter if available ex. today,inplay,streaming
            if (sportCategory !== lastURLSegment) {
                this.subCategory = lastURLSegment;
            }

            return sportCategory;
        }

        const highlightsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportHighlights(sportId));
        if (location.pathname.startsWith(highlightsPath)) {
            return 'HIGHLIGHTS';
        }

        const futurePath = this.routeResolver.getRoute(NavigationPathBuilder.toSportFuture(sportId));
        if (location.pathname.startsWith(futurePath)) {
            return 'FUTURE';
        }

        const priceBoostPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportPriceBoosts(sportId));
        if (location.pathname.startsWith(priceBoostPath)) {
            return 'PRICE_BOOSTS';
        }
    }

    getTranslation(data, suffix) {
        let phrase;
        const {sportId} = data.params;
        const sportSpecific = {
            sportId: sportId.toUpperCase(),
            sportCategory: this.sportCategory,
            subCategory: this.subCategory,
        };
        // prioritize sportSpecific keys over tabSpecific
        const categories = [sportSpecific];
        const prefix = '$SEO.SPORT';

        for (let i = 0; i < categories.length; i++) {
            // create array of object values and remove falsy values
            const currentCategory = Object.values(categories[i]).filter(x => x);
            // iterate through relevant key parts to find first match in dictionary
            // parts example: sportId:soccer sportCategory:MATCHES subCategory:TODAY
            currentCategory.find((_, index, keyParts) => {
                const lookUpKey = [prefix, ...keyParts.slice(0, keyParts.length - index), suffix].join('.');
                const str = this.resolveKey(lookUpKey, data);
                if (str) {
                    phrase = str;

                    return true;
                }
            });
            if (phrase) break;
        }

        if (phrase) return phrase;

        // fallback to default translation
        return this.resolveKey(`$SEO.SPORT.DEFAULT.${suffix}`, data);
    }

    getMeta() {
        if (this.sportCategory === 'OUTRIGHTS' && this.isLeaguePage) {
            return super.getMeta(SEO.meta.outrightLeagues);
        }

        return super.getMeta(SEO.meta.sports);
    }
}

export class EventHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        const {sportId, eventId} = data.params;
        const inplayEventPath = this.routeResolver.getRoute(NavigationPathBuilder.toInplayEvent(eventId));
        let phrase, key;
        if (location.pathname.startsWith(inplayEventPath)) {
            key = `$SEO.EVENT.INPLAY.BY_SPORT.${sportId.toUpperCase()}.${suffix}`;
            phrase = this.resolveKey(key, data);
            if (phrase) {
                return phrase;
            }

            return this.resolveKey(`$SEO.EVENT.INPLAY.DEFAULT.${suffix}`, data);
        } else {
            key = `$SEO.EVENT.PREMATCH.BY_SPORT.${sportId.toUpperCase()}.${suffix}`;
            phrase = this.resolveKey(key, data);
            if (phrase) {
                return phrase;
            }

            return this.resolveKey(`$SEO.EVENT.PREMATCH.DEFAULT.${suffix}`, data);
        }
    }

    getMeta() {
        return super.getMeta(SEO.meta.events);
    }
}

export class RaceMeetingHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.races);
    }
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.RACE_MEETING.' + suffix, data);
    }
}

export class FutureRaceHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.FUTURE_RACE.' + suffix, data);
    }
}

export class VirtualsHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.virtuals);
    }
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.VIRTUAL_SPORTS.' + suffix, data);
    }
}

export class InplayHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.inplay);
    }
    getTranslation(data, suffix) {
        const {sportId} = data.params;
        if (!sportId) {
            // No sportId meaning root (ALL) page
            return this.resolveKey('$SEO.INPLAY.ALL.' + suffix, data);
        }

        return this.resolveKey(`$SEO.INPLAY.${sportId.toUpperCase()}.${suffix}`, data);
    }
}

export class SportCouponHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        const {sportId, couponId} = data.params;

        const phrase = this.resolveKey(
            `$SEO.COUPON.${couponId.toUpperCase()}.${sportId.toUpperCase()}.${suffix}`,
            data
        );
        if (phrase) return phrase;

        return this.resolveKey(`$SEO.COUPON.DEFAULT.${suffix}`, data);
    }
    getMeta() {
        return super.getMeta(SEO.meta.coupons);
    }
}

export class EmptyContentHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.emptyContent);
    }
}

export class CustomPageHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.CUSTOM_PAGE.' + suffix, data);
    }
}

export class PriceBoostHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        return this.resolveKey('$SEO.PRICE_BOOST.' + suffix, data);
    }
}

export class SingleLeagueHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        const {sportId, type: competitionId} = data.params;
        let phrase, key;
        if (competitionId) {
            key = `$SEO.LEAGUE.BY_ID.${competitionId}.${suffix}`;
            phrase = this.resolveKey(key, data);
            if (phrase) {
                return phrase;
            }
        }
        key = `$SEO.LEAGUE.BY_SPORT.${sportId.toUpperCase()}.${suffix}`;
        phrase = this.resolveKey(key, data);
        if (phrase) {
            return phrase;
        }

        key = `$SEO.LEAGUE.DEFAULT.${suffix}`;

        return this.resolveKey(key, data);
    }
}

// Do not generate anything.
export class MyAccountHeadMetaFactory extends BaseHeadMetaFactory {
    getCanonical() {
        return null;
    }
    getRelativeCanonical() {
        return null;
    }
    getTranslation() {
        return '';
    }
    getMeta() {
        return super.getMeta(SEO.meta.myAccount);
    }
    getOpenGraphImages() {
        return [];
    }
}

// Generic factory for lotto-related pages. Could be split into more factories once we have any specific SEO requirements
export class LottoPageHeadMetaFactory extends BaseHeadMetaFactory {
    getTranslation(data, suffix) {
        return this.resolveKey('$LOTTERIES_PAGE.PAGE_' + suffix, data);
    }
}
